import type {
    CardDto,
    ExamResultDto,
    ExamSessionDto,
    ExamSummaryDto,
    MistakeDto,
    QuestionDto,
    QuestionWithAnalysisDto,
    Rating,
    ReviewResultDto,
    StatsResponseDto,
    StudyQueueDto,
    TopicDetailDto,
    TopicSummaryDto,
    UserSettingsDto
} from "@eb/shared";

/**
 * Thin fetch wrapper around the Edge Function API.
 *
 * Supabase Auth owns the session now, so this module no longer manages tokens: it asks
 * supabase-js for the current access token before each request, which transparently
 * refreshes it when it is close to expiry. A 401 therefore means the session is really
 * gone, and the auth provider signs the learner out rather than retrying.
 */

import { API_BASE, SUPABASE_ANON_KEY, supabase } from "./supabase";

export class ApiError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(status: number, code: string, message: string) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.code = code;
    }
}

async function parseError(response: Response): Promise<ApiError> {
    try {
        const body = (await response.json()) as { error?: string; message?: string };
        return new ApiError(response.status, body.error ?? "error", body.message ?? "请求失败");
    } catch {
        return new ApiError(response.status, "error", `请求失败（${response.status}）`);
    }
}

interface RequestOptions {
    method?: string;
    body?: unknown;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    // getSession() refreshes the token when it is near expiry, so callers never have to
    // think about it and there is no retry-on-401 dance to get wrong.
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;

    const headers: Record<string, string> = {
        // The gateway wants the project key; the function wants the user's token.
        apikey: SUPABASE_ANON_KEY,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(options.body === undefined ? {} : { "content-type": "application/json" })
    };

    const response = await fetch(`${API_BASE}${path}`, {
        method: options.method ?? "GET",
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) })
    });

    if (!response.ok) throw await parseError(response);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
}

/** Minutes to add to UTC to reach the browser's local day. */
export function tzOffsetMinutes(): number {
    return -new Date().getTimezoneOffset();
}

function query(params: Record<string, string | number | boolean | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== "") search.set(key, String(value));
    }
    const text = search.toString();
    return text ? `?${text}` : "";
}

export const api = {
    study: {
        queue: (params: { filter?: string; limit?: number; scope?: string; topic?: string }) =>
            request<StudyQueueDto>(
                `/study/queue${query({ ...params, tzOffsetMinutes: tzOffsetMinutes() })}`
            ),
        review: (body: {
            questionId: number;
            rating: Rating;
            mode: "FLASHCARD" | "QUIZ" | "EXAM";
            durationMs: number;
            selectedOptionId: number | null;
        }) => request<ReviewResultDto>("/study/review", { method: "POST", body })
    },

    questions: {
        list: (params: {
            search?: string;
            scope?: string;
            state?: string;
            starred?: boolean;
            onlyImages?: boolean;
            page?: number;
            pageSize?: number;
        }) =>
            request<{
                total: number;
                page: number;
                pageSize: number;
                items: { question: QuestionDto; card: CardDto | null }[];
            }>(`/questions${query(params)}`),
        get: (number: number) =>
            request<{ question: QuestionWithAnalysisDto; card: CardDto | null }>(`/questions/${number}`),
        star: (number: number, starred: boolean) =>
            request<{ card: CardDto }>(`/questions/${number}/star`, { method: "PUT", body: { starred } })
    },

    topics: {
        list: () => request<{ topics: TopicSummaryDto[] }>("/topics"),
        get: (slug: string) => request<{ topic: TopicDetailDto }>(`/topics/${encodeURIComponent(slug)}`)
    },

    mistakes: {
        list: () => request<{ items: MistakeDto[] }>("/mistakes"),
        remove: (number: number) => request<{ ok: true }>(`/mistakes/${number}`, { method: "DELETE" })
    },

    exams: {
        list: () => request<{ sessions: ExamSummaryDto[]; activeId: string | null }>("/exams"),
        create: () => request<ExamSessionDto>("/exams", { method: "POST" }),
        get: (id: string) => request<ExamSessionDto>(`/exams/${id}`),
        answer: (id: string, position: number, optionId: number | null) =>
            request<{ ok: true }>(`/exams/${id}/answer`, { method: "PUT", body: { position, optionId } }),
        submit: (id: string) => request<ExamResultDto>(`/exams/${id}/submit`, { method: "POST" }),
        result: (id: string) => request<ExamResultDto>(`/exams/${id}/result`)
    },

    settings: {
        get: () => request<{ settings: UserSettingsDto }>("/settings"),
        update: (body: Partial<UserSettingsDto>) =>
            request<{ settings: UserSettingsDto }>("/settings", { method: "PATCH", body })
    },

    stats: (days = 180) =>
        request<StatsResponseDto>(`/stats${query({ tzOffsetMinutes: tzOffsetMinutes(), days })}`)
};
